import { Col, FeedItem, ListEmpty, ListSection, SkeletonRows } from "@rally/ui-kit";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { say } from "../localization/say.ts";
import { read } from "../net/rally.ts";
import { type Happening, howToReadIt, whereItHappened, whoDidIt } from "./what-happened.ts";

export type FeedScope = "server" | "project" | "conversation" | "personal";

export function Feed({
  scope = "server",
  id,
  title,
  blurb,
  limit = 40,
  onOpen,
  emptyAction,
}: {
  scope?: FeedScope;
  id?: string;
  title?: string;
  blurb?: string;
  limit?: number;
  onOpen?: (happening: Happening) => void;
  emptyAction?: ReactNode;
}) {
  const [happenings, setHappenings] = useState<Happening[] | null>(null);

  const load = useCallback(async () => {
    const asks: Record<string, string> = { scope, limit: String(limit) };
    if (id) asks.id = id;
    setHappenings(await read<Happening[]>("feed.read", asks));
  }, [scope, id, limit]);

  useEffect(() => {
    void load().catch(() => setHappenings([]));
  }, [load]);

  if (happenings === null) {
    return (
      <ListSection title={title} blurb={blurb}>
        <SkeletonRows rows={5} face={36} />
      </ListSection>
    );
  }

  if (happenings.length === 0) {
    return (
      <ListSection title={title} blurb={blurb}>
        <ListEmpty fill icon="activity" line={say("feed.empty")} action={emptyAction} />
      </ListSection>
    );
  }

  const groups = grouped(happenings);
  return (
    <ListSection title={title} blurb={blurb}>
      <Col gap="none" style={{ width: "100%", maxWidth: 960 }}>
        {groups.map((group, index) => {
          const happening = group[0] as Happening;
          const reading = howToReadIt(happening);
          const lines = linesOf(group);
          return (
            <FeedItem
              key={happening.id}
              who={whoDidIt(happening)}
              face={happening.by.avatar ? `/o/${happening.by.avatar}` : undefined}
              tone={reading.tone}
              state={
                group.length === 1 && Date.now() - new Date(happening.at).getTime() < 5 * 60 * 1000
                  ? (reading.state ?? undefined)
                  : undefined
              }
              at={new Date(happening.at)}
              line={lines}
              where={scope === "conversation" ? undefined : whereItHappened(happening)}
              last={index === groups.length - 1}
              onPress={onOpen ? () => onOpen(happening) : undefined}
            />
          );
        })}
      </Col>
    </ListSection>
  );
}

function grouped(happenings: Happening[]): Happening[][] {
  const groups: Happening[][] = [];
  for (const happening of happenings) {
    const last = groups[groups.length - 1];
    const first = last?.[0];
    const same =
      first?.by.account === happening.by.account &&
      first?.conversation?.id === happening.conversation?.id &&
      first?.project?.id === happening.project?.id &&
      (last?.length ?? 0) < 4;
    if (same && last) last.push(happening);
    else groups.push([happening]);
  }
  return groups;
}

function linesOf(group: Happening[]): string {
  if (group.length === 1) return howToReadIt(group[0] as Happening).line;
  const lines = group
    .filter((one) => one.kind !== "run.started" && one.kind !== "run.finished")
    .map((one) => howToReadIt(one).line)
    .filter((line, at, all) => all.indexOf(line) === at);
  if (group.some((one) => one.kind === "run.finished")) lines.push(say("happened.completedARun"));
  return lines.slice(0, 3).join(" · ");
}
